import { useMutation } from "@tanstack/react-query";
import { Camera, ImagePlus, Loader2, X } from "lucide-react";
import React, { useRef, useState } from "react";
import { toast } from "sonner";

import {
  deletePhoto,
  fetchOkkPhotoUrl,
  uploadPhoto,
  type PhotoView,
} from "@/api/okk_receiving";
import { PhotoCapture } from "@/components/PhotoCapture";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/useAuth";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { useUploadProgress } from "@/lib/use-upload-progress";
import { LazyThumb } from "@/pages/order-journal/parts";

/** Что принимает сервер (`PHOTO_FORMATS`): HEIC он отобьёт 415 с подсказкой. */
export const GALLERY_ACCEPT = "image/jpeg,image/png,image/webp";

/**
 * Фото проверки (к позиции или общие): превью сеткой, «Снять» камерой и
 * «Из галереи» пачкой — ОРИГИНАЛОМ, без ужатия. Отказ сервера (413 — большой
 * файл, 415 — формат/HEIC) — его текстом под блоком, по каждому файлу.
 */
export function OkkPhotoBlock({
  inspectionId,
  itemId,
  title,
  photos,
  locked,
  onOpen,
  onChanged,
}: {
  inspectionId: string;
  itemId: string | null;
  /** Заголовок камеры («Фото: <позиция>»). */
  title: string;
  photos: PhotoView[];
  /** Проверка завершена / позиция ушла из прихода — только просмотр. */
  locked: boolean;
  onOpen: (index: number) => void;
  /** Фото добавлено или удалено — перечитать карточку. */
  onChanged: () => void;
}): React.ReactElement {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [camera, setCamera] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const upload = useUploadProgress();
  useBackGuard(camera, () => setCamera(false));

  const send = useMutation({
    mutationFn: async (files: { blob: Blob; name: string }[]) => {
      const failed: string[] = [];
      for (const f of files) {
        try {
          await uploadPhoto(inspectionId, f.blob, {
            itemId,
            fileName: f.name,
            onProgress: upload.start(),
          });
        } catch (e) {
          failed.push(`${f.name}: ${apiErrorText(e, "не загрузилось")}`);
        }
      }
      return { failed, sent: files.length - failed.length };
    },
    onMutate: () => setErrors([]),
    onSuccess: ({ failed, sent }) => {
      setErrors(failed);
      // Камеру закрываем, только если хоть что-то ушло: при отказе сервера
      // она остаётся открытой, и кадр можно снять заново, а не терять.
      if (sent > 0) {
        setCamera(false);
        onChanged();
      }
      // Поверх открытой камеры блок ошибок не виден — показываем тостом.
      if (failed.length > 0 && camera) toast.error(failed.join("\n"));
    },
    onSettled: () => upload.reset(),
  });

  const remove = useMutation({
    mutationFn: (photoId: string) => deletePhoto(inspectionId, photoId),
    onMutate: () => setErrors([]),
    onSuccess: () => onChanged(),
    onError: (e) => setErrors([`Фото не удалено: ${apiErrorText(e)}`]),
  });

  return (
    <div className="space-y-2">
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p, i) => {
            const mine = Boolean(user && (user.is_admin || p.uploaded_by?.id === user.id));
            return (
              <div key={p.id} className="relative">
                <LazyThumb
                  cacheKey={`okk-thumb:${p.id}`}
                  load={() => fetchOkkPhotoUrl(p.id, true)}
                  onClick={() => onOpen(i)}
                  label={`Фото ${i + 1}`}
                />
                {!locked && mine && (
                  <button
                    type="button"
                    aria-label={`Удалить фото ${i + 1}`}
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm("Удалить фото?")) remove.mutate(p.id);
                    }}
                    className="absolute -right-1.5 -top-1.5 flex size-6 items-center justify-center rounded-full border bg-background text-rose-700 shadow hover:bg-rose-50"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!locked && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCamera(true)}
            disabled={send.isPending}
          >
            <Camera />
            Снять
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={send.isPending}
          >
            {send.isPending && !camera ? <Loader2 className="animate-spin" /> : <ImagePlus />}
            Из галереи
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept={GALLERY_ACCEPT}
            multiple
            className="hidden"
            data-testid={`okk-gallery-input-${itemId ?? "general"}`}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              // Сброс значения — чтобы тот же файл можно было выбрать снова.
              e.target.value = "";
              if (files.length) send.mutate(files.map((f) => ({ blob: f, name: f.name })));
            }}
          />
          {send.isPending && !camera && upload.progress !== null && (
            <span className="self-center text-xs tabular-nums text-muted-foreground">
              {Math.round(upload.progress)}%
            </span>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}

      {camera && (
        <PhotoCapture
          title={title}
          onCapture={(blob) => send.mutate([{ blob, name: "camera.jpg" }])}
          onClose={() => setCamera(false)}
          busy={send.isPending}
          progress={upload.progress}
        />
      )}
    </div>
  );
}
